import { useCallback } from 'react';
import { useRelayEnvironment, type GraphQLTaggedNode } from 'react-relay';
import { type DocumentNode } from 'graphql/language/ast';
import { useApolloClient } from '@apollo/react-hooks';
import { type ReaderFragment, type IEnvironment, type ReaderSelection } from 'relay-runtime';

type RelayUseReadFragmentInteropParams = { id?: string; fragmentSpec: GraphQLTaggedNode };
type ApolloUseReadFragmentInteropParams = { id?: string; fragmentSpec: DocumentNode };

export type UseReadFragmentInteropParams = {
	id?: string;
	fragmentSpec: DocumentNode | GraphQLTaggedNode;
};

export type FragmentReaderInteropParams<TData> = { id?: string; data: TData };
export type FragmentReaderInterop<TData> = (params?: { id?: string }) => TData | null;

export type UseReadFragmentInteropHook<TData> = (
	params: UseReadFragmentInteropParams,
) => FragmentReaderInterop<TData>;

export function __test_only_useReadFragmentInterop__Relay<TData>(
	params: RelayUseReadFragmentInteropParams,
): FragmentReaderInterop<TData> {
	const { fragmentSpec, id: defaultId } = params;
	const env = useRelayEnvironment();

	return useCallback(
		(callbackParams?: { id?: string }) => {
			const id = callbackParams?.id ?? defaultId;
			if (!id) {
				return null;
			}
			return readFragmentShim(env, fragmentSpec, id);
		},
		[env, fragmentSpec, defaultId],
	);
}

export function __test_only_useReadFragmentInterop__Apollo<TData>(
	params: ApolloUseReadFragmentInteropParams,
): FragmentReaderInterop<TData> {
	const { fragmentSpec: fragment, id: defaultId } = params;

	const client = useApolloClient();

	return useCallback(
		(callbackParams?: { id?: string }) => {
			const id = callbackParams?.id ?? defaultId;
			if (!id) {
				return null;
			}
			const data = client.readFragment<TData>({
				id,
				fragment,
			});

			return data as TData;
		},
		[client, fragment, defaultId],
	);
}

export function useReadFragmentInterop<TData>(
	params: UseReadFragmentInteropParams,
): FragmentReaderInterop<TData> {
	return process.env.BUILD_VARIANT === 'relay'
		? __test_only_useReadFragmentInterop__Relay(
				params as UseReadFragmentInteropParams & RelayUseReadFragmentInteropParams,
			)
		: __test_only_useReadFragmentInterop__Apollo(
				params as UseReadFragmentInteropParams & ApolloUseReadFragmentInteropParams,
			);
}

function apolloFragmentIdToRelayFragmentId(apolloFragmentId: string) {
	return apolloFragmentId.replace(/^\$ROOT_QUERY\./, 'client:root:');
}

/**
 * Shim function that mimics Apollo's `readFragment` using Relay
 */
function readFragmentShim<TData>(
	environment: IEnvironment,
	fragment: GraphQLTaggedNode,
	fragmentId: string,
): TData | null {
	// Get the store from the environment
	const store = environment.getStore();

	// Get the source from the store
	const source = store.getSource();

	// Get the record using the fragmentId
	const rootRecord = source.get(apolloFragmentIdToRelayFragmentId(fragmentId));

	if (!rootRecord) {
		return null;
	}

	// Define a function to read the fields specified in the fragment
	const readFields = (recordId: string, fields: readonly ReaderSelection[]): any => {
		const record = source.get(recordId);
		if (!record) {
			return null;
		}

		const data = {} as any;
		for (const field of fields) {
			if (field.kind === 'LinkedField') {
				// For linked fields, we need to get the reference and recursively read
				const linkedRecordRef = record[field.name];
				if (linkedRecordRef && typeof linkedRecordRef === 'object' && '__ref' in linkedRecordRef) {
					const linkedRecordId = (linkedRecordRef as { __ref: string }).__ref;
					data[field.name] = readFields(linkedRecordId, field.selections);
				} else {
					data[field.name] = null;
				}
			} else if (field.kind === 'ScalarField') {
				data[field.name] = record[field.name];
			} else {
				throw new Error('Unsupported field');
			}
		}
		return data;
	};

	const readerFragment = fragment as ReaderFragment;

	const topLevelFields = readerFragment?.selections;

	return readFields(apolloFragmentIdToRelayFragmentId(fragmentId), topLevelFields ?? []);
}
